/**
 * Service for managing Google Calendar user preferences in Firestore.
 */
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

/**
 * Subscribes to Google Calendar preferences for a specific user.
 *
 * @param {string} userId - The unique identifier of the user.
 * @param {Function} onUpdate - Callback function called with the updated preferences and cache.
 * @param {Function} [onError] - Optional callback function for errors.
 * @returns {Function} Unsubscribe function.
 */
export const subscribeToGCalData = (userId, onUpdate, onError) => {
  if (!userId) return () => {};

  // Moving from root user doc to preferences/settings subcollection document
  const prefDocRef = doc(db, 'users', userId, 'preferences', 'settings');

  return onSnapshot(
    prefDocRef,
    (docSnap) => {
      const data = docSnap.exists() ? docSnap.data() : {};
      onUpdate({
        isEnabled: data.gcalEnabled ?? false,
        events: data.gcalCache?.events || [],
        lastSync: data.gcalCache?.lastSync?.toDate() || null,
      });
    },
    (error) => {
      console.error(
        `[GCalPreferenceService] Error listening to ${prefDocRef.path}:`,
        error,
      );
      if (onError) onError(error);
    },
  );
};

/**
 * Updates the Google Calendar enabled preference for a user.
 *
 * @param {string} userId - The unique identifier of the user.
 * @param {boolean} isEnabled - The new enabled state.
 */
export const updateGCalEnabledPreference = async (userId, isEnabled) => {
  if (!userId) return;

  const prefDocRef = doc(db, 'users', userId, 'preferences', 'settings');

  try {
    await setDoc(
      prefDocRef,
      {
        gcalEnabled: isEnabled,
      },
      { merge: true },
    );
  } catch (error) {
    console.error(
      `[GCalPreferenceService] Permission denied or error writing to ${prefDocRef.path}:`,
      error,
    );
    throw error;
  }
};
